import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import db from '../firebase/firebaseConfig';
import { getGameById } from '../firebase/db';
import ResearchDevelopmentTable from './ResearchDevelopmentTable';
import tradeoffMatrix from '../data/tradeoffMatrix.json';
import AdvertisingTable from './AdvertisingTable';
import ProfitMarginTable from './ProfitMarginTable';
import styles from '../styles/DecisionTool.styles';
import { StartButton } from '../styles/CaseStudyHome.styles';
import SubmissionMessage from './SubmissionMessage';

const DecisionTool3 = ({ teamId }) => {
    const [isRound2Submitted, setIsRound2Submitted] = useState(false);
    const [loading, setLoading] = useState(true);
    // const { teamId } = useParams();
    const [roundNumber, setRoundNumber] = useState(3);
    const [totalSum, setTotalSum] = useState(0);

    // Parse the fullTeamId into teamId and submissionType
    const teamIdParts = teamId.split("_");
    const submissionType = teamIdParts.pop();
    const teamIdParsed = teamIdParts.join("_");



    const [isSubmitted, setIsSubmitted] = useState(false); // Track submission status
    const [isRoundLocked, setIsRoundLocked] = useState(false);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(null);
    const [manufacturedBikeValues, setManufacturedBikeValues] = useState({});

    const [errorMessage, setErrorMessage] = useState('');
    const [firestoreError, setFirestoreError] = useState('');


    // Helper function to generate unique session keys for each team
    const getSessionKey = (key) => `${teamId}_${key}`;
    // const getSessionKey = (round, key) => `${teamId}_round${round}_${key}`;


    const [selectedSegments, setSelectedSegments] = useState(() => {
        return JSON.parse(sessionStorage.getItem(getSessionKey('selectedSegments'))) || [];
    });
    const [profitMarginLevel, setProfitMarginLevel] = useState(() => {
        return JSON.parse(sessionStorage.getItem(getSessionKey('profitMarginLevel'))) || 1; // Default value of 1
    });

    const segmentsOptions = [
        "All Potential",
        "Urban Commuters",
        "Tourism Agencies",
        "Rural",
        "Delivery/Logistics",
        "Health/Fitness",
        "Education",
        "Government"
    ];

    const [selectedLevels, setSelectedLevels] = useState(() => {
        const storedLevels = sessionStorage.getItem(getSessionKey('selectedLevels'));
        return storedLevels ? JSON.parse(storedLevels) : {
            BatteryRange: 1,
            Comfort: 1,
            SmartFeatures: 1,
            Security: 1,
            Safety: 1,
            Speed: 1,
            Storage: 1,
            AllTerrain: 1,
        };
    });

    const [rdUpgrades, setRdUpgrades] = useState(() => {
        const storedUpgrades = sessionStorage.getItem(getSessionKey('rdUpgrades'));
        return storedUpgrades ? JSON.parse(storedUpgrades) : {
            BatteryRange: 0,
            Comfort: 0,
            SmartFeatures: 0,
            Security: 0,
            Safety: 0,
            Speed: 0,
            Storage: 0,
            AllTerrain: 0,
        };
    });

    const [advertisingBudgets, setAdvertisingBudgets] = useState(() => {
        const storedBudgets = sessionStorage.getItem(getSessionKey('advertisingBudgets'));
        return storedBudgets ? JSON.parse(storedBudgets) : {
            Performance: {
                BatteryRange: 0,
                Comfort: 0,
                SmartFeatures: 0,
                Security: 0,
                Safety: 0,
                Speed: 0,
                Storage: 0,
                AllTerrain: 0,
                Broad: 0,
            },
            Brand: {
                BatteryRange: 0,
                Comfort: 0,
                SmartFeatures: 0,
                Security: 0,
                Safety: 0,
                Speed: 0,
                Storage: 0,
                AllTerrain: 0,
                Broad: 0,
            },
        };
    });

    useEffect(() => {
        if (isConfirmationVisible || firestoreError || errorMessage) {
            // Scroll to bottom of the page smoothly
            window.scrollTo({
                top: document.documentElement.scrollHeight,
                behavior: "smooth",
            });
        }
    }, [errorMessage, firestoreError, isConfirmationVisible]);

    const handleSegmentSelect = (event) => {
        const segment = event.target.value;

        if (segment === "All Potential") {
            // Add all segments (except "All Potential") to the selectedSegments list
            const allSegments = segmentsOptions.filter(s => s !== "All Potential");
            setSelectedSegments(allSegments);
        } else {
            // If the segment is not already selected and it's not "All Potential", add it to the list
            if (!selectedSegments.includes(segment) && segment !== "") {
                setSelectedSegments([...selectedSegments, segment]);
            }
        }
    };

    useEffect(() => {
        const fetchSubmissionStatus = async () => {
            try {
                const docRef = doc(db, submissionType, teamIdParsed);
                const docSnap = await getDoc(docRef);
                // if (docSnap.exists() && docSnap.data().is_submitted) {
                //     setIsSubmitted(docSnap.data().is_submitted);
                // }
                if (docSnap.exists()) {
                    const teamData = docSnap.data();
                    const round2Data = teamData.rounds && teamData.rounds[2];

                    if (round2Data && round2Data.is_submitted) {
                        setIsSubmitted(round2Data.is_submitted);
                    }

                    if (teamData.gameId) {
                        const game = await getGameById(teamData.gameId);
                        if (game?.lockedRounds?.includes(3)) {
                            setIsRoundLocked(true);
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching submission status:', error);
                setFirestoreError('Error fetching submission status');
            } finally {
                setLoading(false);
            }
        };
        fetchSubmissionStatus();
    }, [teamId]);

    const handleSegmentRemove = (segmentToRemove) => {
        setSelectedSegments(selectedSegments.filter(segment => segment !== segmentToRemove));
    };

    const handleLevelChange = (attribute, level) => {
        const updatedSelectedLevels = { ...selectedLevels };
        updatedSelectedLevels[attribute] = parseInt(level);
        setSelectedLevels(updatedSelectedLevels);
    };

    const handleUpgradeChange = (attribute, upgrade) => {
        const updatedRdUpgrades = { ...rdUpgrades };
        updatedRdUpgrades[attribute] = parseFloat(upgrade / 500000);
        setRdUpgrades(updatedRdUpgrades);
    };

    const handleAdvertisingChange = (type, attribute, value) => {
        const updatedAdvertisingBudgets = { ...advertisingBudgets };
        updatedAdvertisingBudgets[type][attribute] = parseFloat(value);
        setAdvertisingBudgets(updatedAdvertisingBudgets);
    };

    const attributesList = Object.keys(tradeoffMatrix.attributes).filter((attribute) => attribute !== "Price");

    const calculateUpgradedBikeValues = () => {
        const upgradedBikeValues = {};

        attributesList.forEach((attribute) => {
            const upgradedValue = manufacturedBikeValues[attribute] + rdUpgrades[attribute];
            upgradedBikeValues[attribute] = Math.min(Math.max(upgradedValue, 1), 5);
        });

        return upgradedBikeValues;
    };

    const upgradedBikeValues = calculateUpgradedBikeValues();

    const fetchRound2Data = async () => {
        try {
            const teamDocRef = doc(db, submissionType, teamIdParsed);
            const teamSnap = await getDoc(teamDocRef);
            const teamData = teamSnap.exists() ? teamSnap.data() : null;

            if (teamData) {
                const round2Data = teamData.rounds[1];
                const round2ManufacturedBikeValues = round2Data?.manufacturing?.upgraded_bike || {};
                // const round1RdUpgrades = round1Data?.rnd?.upgrades || {};
                const round2TotalSum = round2Data?.profit_margin.cost_per_bike_dollars / 10 || 0;
                setTotalSum(round2TotalSum);
                //console.log('round2:', round2TotalSum);
                // Calculate and set Round 2 manufactured bike values
                // const updatedValues = calculateRound2ManufacturedBikeValues(round1ManufacturedBikeValues, round1RdUpgrades);
                setManufacturedBikeValues(round2ManufacturedBikeValues);
                //console.log('Updated values for Round 2:', round2ManufacturedBikeValues);
            } else {
                console.log('No team data found.');
            }
        } catch (error) {
            console.error('Error fetching Round 1 data:', error);
        }
    };

    // Calculate Round 2 Manufactured Bike Values
    const calculateRound3ManufacturedBikeValues = (round2ManufacturedBikeValues, round2RdUpgrades) => {
        const updatedManufacturedBikeValues = {};

        // Add R&D upgrades to the Round 1 manufactured bike values
        attributesList.forEach((attribute) => {
            const round2Value = round2ManufacturedBikeValues[attribute];
            const rdUpgradeValue = round2RdUpgrades[attribute] || 0; // Default to 0 if no R&D upgrade exists
            updatedManufacturedBikeValues[attribute] = Math.min(Math.max(round2Value + rdUpgradeValue, 0), 5); // Ensure values are between 0 and 5
        });

        return updatedManufacturedBikeValues;
    };

    useEffect(() => {
        fetchRound2Data();
    }, [teamId]); // Only re-fetch if the teamId changes

    useEffect(() => {
        //('setting round 2 submitted to true');
        const checkRound2Submission = async () => {
            if (!teamId) {
                console.error("Error: teamId is undefined.");
                setLoading(false);
                return;
            }

            try {
                const docRef = doc(db, submissionType, teamIdParsed);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const teamData = docSnap.data();
                    const round2Data = teamData.rounds && teamData.rounds[1];

                    if (round2Data && round2Data.is_submitted) {
                        setIsRound2Submitted(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching submission status:', error);
            } finally {
                setLoading(false);
            }
        };

        checkRound2Submission();
    }, [teamId]);

    // Reset session storage and state when Round 1 is submitted
    useEffect(() => {
        if (isRound2Submitted) {
            //console.log('resetting state');
            // Clear sessionStorage for Round 3 keys
            sessionStorage.removeItem(getSessionKey('selectedSegments'));
            sessionStorage.removeItem(getSessionKey('profitMarginLevel'));
            sessionStorage.removeItem(getSessionKey('selectedLevels'));
            sessionStorage.removeItem(getSessionKey('rdUpgrades'));
            sessionStorage.removeItem(getSessionKey('advertisingBudgets'));

            // Reinitialize state to its default values
            setSelectedSegments([]);  // Default value for selectedSegments
            setProfitMarginLevel(1);  // Default value for profitMarginLevel
            setSelectedLevels({
                BatteryRange: 1,
                Comfort: 1,
                SmartFeatures: 1,
                Security: 1,
                Safety: 1,
                Speed: 1,
                Storage: 1,
                AllTerrain: 1,
            }); // Default values for selectedLevels
            setRdUpgrades({
                BatteryRange: 0,
                Comfort: 0,
                SmartFeatures: 0,
                Security: 0,
                Safety: 0,
                Speed: 0,
                Storage: 0,
                AllTerrain: 0,
            }); // Default values for rdUpgrades
            setAdvertisingBudgets({
                Performance: {
                    BatteryRange: 0,
                    Comfort: 0,
                    SmartFeatures: 0,
                    Security: 0,
                    Safety: 0,
                    Speed: 0,
                    Storage: 0,
                    AllTerrain: 0,
                    Broad: 0,
                },
                Brand: {
                    BatteryRange: 0,
                    Comfort: 0,
                    SmartFeatures: 0,
                    Security: 0,
                    Safety: 0,
                    Speed: 0,
                    Storage: 0,
                    AllTerrain: 0,
                    Broad: 0,
                },
            }); // Default values for advertisingBudgets
        }
        // Set flag to prevent further resets
    }, [teamId]);

    // Update session storage whenever state changes for Round 2
    useEffect(() => {
        sessionStorage.setItem(getSessionKey('selectedSegments'), JSON.stringify(selectedSegments));
        sessionStorage.setItem(getSessionKey('profitMarginLevel'), JSON.stringify(profitMarginLevel));
        sessionStorage.setItem(getSessionKey('selectedLevels'), JSON.stringify(selectedLevels));
        sessionStorage.setItem(getSessionKey('rdUpgrades'), JSON.stringify(rdUpgrades));
        sessionStorage.setItem(getSessionKey('advertisingBudgets'), JSON.stringify(advertisingBudgets));
    }, [selectedSegments, profitMarginLevel, selectedLevels, rdUpgrades, advertisingBudgets]);

    const handleSubmit = async () => {
        const totalCost = calculateTotalCost(selectedLevels);

        if (validateSubmission()) {
            const dataToSubmit = {
                selectedLevels,
                rdUpgrades,
                advertisingBudgets,
                selectedSegments,
                totalCost,
                upgradedBikeValues,
            };
            console.log("Submitted data:", dataToSubmit);

            try {
                // Reference the team document using teamId
                const teamDocRef = doc(db, submissionType, teamIdParsed);

                // Fetch the current team data to preserve round 2 data
                const teamSnap = await getDoc(teamDocRef);
                const teamData = teamSnap.exists() ? teamSnap.data() : null;

                // Prepare updated rounds data, ensuring round 2 remains intact
                const updatedRounds = teamData?.rounds || [];
                updatedRounds[2] = {
                    roundNumber: 3,
                    manufacturing: {
                        attributes: selectedLevels,
                        total_cost: totalCost,
                        upgraded_bike: upgradedBikeValues,
                    },
                    rnd: {
                        upgrades: rdUpgrades,
                    },
                    advertising: {
                        segments: selectedSegments,
                        performance_ads: advertisingBudgets.Performance,
                        brand_ads: advertisingBudgets.Brand,
                    },
                    profit_margin: {
                        profit_margin_selection: profitMarginLevel,
                        profit_margin_percent: calculateProfitMarginPercentage().toFixed(2),
                        profit_margin_per_bike_dollars: calculateProfitMarginPerBike().toFixed(2),
                        price_per_bike_dollars: calculatePricePerBike().toFixed(2),
                        cost_per_bike_dollars: calculateCostPerBike().toFixed(2),
                    },
                    is_submitted: true,
                };

                // Write back the entire updated rounds array
                await updateDoc(teamDocRef, { rounds: updatedRounds });

                console.log("Data successfully submitted to Firestore.");
                setIsConfirmationVisible(false); // Close the confirmation after submission
                setIsSubmitted(true); // Lock the page on submission
                setFirestoreError('');
            } catch (error) {
                console.error("Error updating Firestore:", error);
                setFirestoreError('Failed to submit data. Please register your team again.');
            }
        }
    };


    const calculateTotalCost = (selectedLevels) => {
        // Add your logic to calculate the total cost based on selectedLevels
        let cost = 0;
        for (let key in selectedLevels) {
            cost += selectedLevels[key];
        }
        return cost;
    };

    const [isRndError, setIsRndError] = useState(false); // error handling state for R&D
    const [totalRndCost, setTotalRndCost] = useState(0); // total R&D cost state

    useEffect(() => {
        // Get the total R&D cost by summing the values of the rdUpgrades object
        const totalCost = Object.values(rdUpgrades).reduce((sum, cost) => sum + cost, 0);

        // Determine the budget based on the current round number
        const maxBudget = roundNumber === 3 ? 2000000 : 1500000;

        // Update the total R&D cost and check for error if it exceeds the max budget
        const totalRndCost = totalCost * 500000;
        setTotalRndCost(totalRndCost);

        // Check if the total R&D cost exceeds the max budget
        setIsRndError(totalRndCost > maxBudget);

    }, [rdUpgrades, roundNumber]); // This effect runs whenever rdUpgrades or roundNumber changes

    const calculateTotalAdCost = () => {
        const performanceTotal = Object.values(advertisingBudgets.Performance).reduce((sum, value) => sum + value, 0);
        const brandTotal = Object.values(advertisingBudgets.Brand).reduce((sum, value) => sum + value, 0);
        return performanceTotal + brandTotal;
    };

    const [isAdError, setIsAdError] = useState(false); // State for error checking

    // Check if the advertising budget exceeds the limit
    useEffect(() => {
        // Calculate the total advertising cost
        const totalAdCost = calculateTotalAdCost();

        // Determine the advertising budget limit based on the round number
        const adBudgetLimit = roundNumber === 3 ? 2000000 : 500000;

        // Check if the total advertising cost exceeds the budget limit
        if (totalAdCost > adBudgetLimit) {
            setIsAdError(true);
        } else {
            setIsAdError(false);
        }
    }, [advertisingBudgets, roundNumber]); // Recalculate when advertising budgets or roundNumber change

    const validateSubmission = () => {
        // Calculate total cost (assuming you have the selected levels available)
        const totalCost = calculateTotalCost(selectedLevels);

        // Check if all advertising budgets are still at 0
        const allBudgetsZero = Object.values(advertisingBudgets).every(brand =>
            Object.values(brand).every(value => value === 0)
        );

        // Show error if segments are empty and not all budgets are zero
        if (selectedSegments.length === 0 && !allBudgetsZero) {
            setErrorMessage('Error! Please select at least one segment to target in advertising.');
            return false; // Validation failed
        }

        // Show error if total cost exceeds 25
        if (totalCost > 25) {
            setErrorMessage('Error! You have exceeded your manufacturing budget of 25 credits.');
            return false; // Validation failed
        }

        // Calculate total R&D budget
        const totalRdBudget = attributesList.reduce((acc, attribute) => {
            return acc + (rdUpgrades[attribute] * 500_000); // Convert R&D input to 500k
        }, 0);

        // Calculate total advertising budget
        const totalAdBudget = attributesList.reduce((acc, attribute) => {
            return acc + advertisingBudgets.Performance[attribute] + advertisingBudgets.Brand[attribute];
        }, 0);

        if (isRndError) {
            setErrorMessage('Error! You have exceeded your R&D budget of $1.5 million.');
            return false;
        }

        if (isAdError) {
            setErrorMessage('Error! You have exceeded your Advertising budget of $500,000.');
            return false;
        }


        // Check if total budget exceeds $2 million
        if (totalRdBudget + totalAdBudget + advertisingBudgets.Performance['Broad'] + advertisingBudgets.Brand['Broad'] > 2_000_000) {
            setErrorMessage('Error! You have exceeded your combined R&D and Ads budget of $2 million.');
            return false;
        }


        setErrorMessage(''); // Clear error message if validation passes
        return true; // Validation passed
    };

    const openConfirmation = () => {
        if (validateSubmission()) {
            setIsConfirmationVisible(true);
        }
    };

    const closeConfirmation = () => {
        setIsConfirmationVisible(false);
    };

    // Calculate the profit margin percentage based on the selected level
    const calculateProfitMarginPercentage = () => profitMarginLevel * 10; // 1 = 10%, 2 = 20%, etc.

    // Calculate cost per bike based on the total cost from manufacturing
    const calculateCostPerBike = () => totalSum * 10; // Assuming `totalSum` is your calculated total cost

    // Calculate profit per bike in dollars
    const calculateProfitMarginPerBike = () => calculateCostPerBike() * (calculateProfitMarginPercentage() / 100);

    // Calculate price per bike based on cost and profit margin
    const calculatePricePerBike = () => calculateCostPerBike() + calculateProfitMarginPerBike();



    if (loading) {
        return <div></div>;
    }

    if (!isRound2Submitted) {
        return (
            <div>
                <h2>Round 3 Locked</h2>
                <p>Please finish Round 2 to access Round 3.</p>
            </div>
        );
    }

    if (isSubmitted) {
        return <SubmissionMessage roundNumber={3} teamId={teamId} />;
    }

    if (isRoundLocked) {
        return (
            <div style={{ maxWidth: '600px', margin: '80px auto', padding: '40px', textAlign: 'center', fontFamily: 'Arial, sans-serif' }}>
                <h2 style={{ color: '#e65100' }}>Round 3 is Locked</h2>
                <p style={{ color: '#666', fontSize: '16px' }}>Your instructor has locked this round. Submissions are not currently accepted.</p>
            </div>
        );
    }

    return (
        <div>
            <h2> Round 3</h2>
            <ResearchDevelopmentTable
                attributesList={attributesList}
                manufacturedBikeValues={manufacturedBikeValues}
                rdUpgrades={rdUpgrades}
                upgradedBikeValues={upgradedBikeValues}
                handleUpgradeChange={handleUpgradeChange}
                budget={2}
                roundNumber={roundNumber}
            />
            <AdvertisingTable
                segmentsOptions={segmentsOptions}
                selectedSegments={selectedSegments}
                handleSegmentSelect={handleSegmentSelect}
                handleSegmentRemove={handleSegmentRemove}
                attributesList={attributesList}
                upgradedBikeValues={upgradedBikeValues}
                advertisingBudgets={advertisingBudgets}
                handleAdvertisingChange={handleAdvertisingChange}
                roundNumber={roundNumber}
            />

            <ProfitMarginTable
                profitMarginLevel={profitMarginLevel}
                setProfitMarginLevel={setProfitMarginLevel}
                calculateProfitMarginPercentage={calculateProfitMarginPercentage}
                calculateCostPerBike={calculateCostPerBike}
                calculateProfitMarginPerBike={calculateProfitMarginPerBike}
                calculatePricePerBike={calculatePricePerBike}
                roundNumber={roundNumber}
            />
            {/* <ProfitMarginTable
                profitMarginLevel={profitMarginLevel}
                setProfitMarginLevel={setProfitMarginLevel}
                calculateProfitMarginPercentage={calculateProfitMarginPercentage().toFixed(2)}
                calculateCostPerBike={calculateCostPerBike().toFixed(2)}
                calculateProfitMarginPerBike={calculateProfitMarginPerBike().toFixed(2)}
                calculatePricePerBike={calculatePricePerBike().toFixed(2)}
                roundNumber={roundNumber}
            /> */}

            {/* {!loading && totalSum !== 0 && (
                <ProfitMarginTable
                    profitMarginLevel={profitMarginLevel}
                    setProfitMarginLevel={setProfitMarginLevel}
                    calculateProfitMarginPercentage={calculateProfitMarginPercentage}
                    calculateCostPerBike={calculateCostPerBike}
                    calculateProfitMarginPerBike={calculateProfitMarginPerBike}
                    calculatePricePerBike={calculatePricePerBike}
                    roundNumber={roundNumber}
                />
            )} */}

            {/* SUBMIT BUTTON */}

            <div>
                {/* <styles.SubmitButton onClick={openConfirmation}>Submit</styles.SubmitButton> */}
                <StartButton onClick={openConfirmation}>Submit</StartButton>
            </div>


            {/* Confirmation Popup */}
            {isConfirmationVisible && (
                <div className={styles.confirmationPopup}>
                    <p> </p>
                    <h3>Are you sure you want to Submit? All Submissions are FINAL.</h3>
                    <button
                        onClick={handleSubmit}
                        style={{ marginBottom: '12px' }}
                    >Yes</button>
                    <> </>
                    <button
                        onClick={closeConfirmation}
                        style={{ marginBottom: '12px' }}
                    >No</button>
                </div>
            )}

            {/* Error message displayed under the submit button */}
            {firestoreError && <styles.errorMessage>{firestoreError}</styles.errorMessage>}
            {errorMessage && <styles.errorMessage>{errorMessage}</styles.errorMessage>}

        </div>
    );
};

export default DecisionTool3;
